import React, { useEffect, useState } from "react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Lock, ArrowRight, ShoppingBag, ArrowLeft } from "lucide-react";

function ResetPassword() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    newPassword: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const isValidPassword = formData.newPassword.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    } else if (!isValidPassword) {
      newErrors.confirmPassword =
        "Confirm Password must be at least 6 characters";
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.general = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (!token) {
      setMessage("Invalid reset link.");
      return;
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const res = await userAPI.resetPassword(token, formData);
      if (res.code === 200) {
        showSuccess(res.message);
        navigate("/users/login");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      {/* Left panel — decorative */}
      <div className="auth-panel auth-panel--left">
        <div className="auth-panel__overlay" />
        <div className="auth-panel__content">
          <div className="auth-brand">
            <ShoppingBag size={32} color="#C9991E" />
            <span>Premium Store</span>
          </div>
          <h2 className="auth-panel__headline">
            Almost there. Set a new password.
          </h2>
          <p className="auth-panel__sub">
            Choose a strong new password to keep your account safe and
            secure.
          </p>
          <div className="auth-panel__dots">
            <span className="dot dot--active" />
            <span className="dot" />
            <span className="dot" />
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="auth-panel auth-panel--right">
        <div className="auth-form-box">
          <Link to="/users/login" className="auth-back-link">
            <ArrowLeft size={15} />
            Back to Login
          </Link>

          <p className="auth-form-eyebrow">Account Security</p>
          <h1 className="auth-form-title">Reset Password</h1>
          <p className="auth-form-subtitle">
            Enter your new password below to continue
          </p>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            {/* New Password */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="newPassword">
                New Password
              </label>
              <div
                className={`auth-input-wrap ${
                  errors.newPassword ? "has-error" : ""
                }`}
              >
                <Lock size={17} className="auth-input-icon" />
                <input
                  id="newPassword"
                  type="password"
                  name="newPassword"
                  placeholder="Min. 6 characters"
                  value={formData.newPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
              </div>
              {errors.newPassword && (
                <span className="auth-error">{errors.newPassword}</span>
              )}
            </div>

            {/* Confirm Password */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="confirmPassword">
                Confirm Password
              </label>
              <div
                className={`auth-input-wrap ${
                  errors.confirmPassword ? "has-error" : ""
                }`}
              >
                <Lock size={17} className="auth-input-icon" />
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  placeholder="Re-enter new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
              </div>
              {errors.confirmPassword && (
                <span className="auth-error">{errors.confirmPassword}</span>
              )}
            </div>

            {errors.general && (
              <span className="auth-error">{errors.general}</span>
            )}
            {message && <span className="auth-error">{message}</span>}

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  Reset Password <ArrowRight size={17} className="ms-2" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;